import startCase from 'lodash/startCase';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';
import Config from 'config';
import StorageUtils from '@spothero/utils/storage';
import GoogleRemarketingUtils from 'utils/google-remarketing';
import GTMUtils from 'utils/gtm';
import SearchTracking from 'utils/search-tracking';
import SegmentUtils from 'utils/segment';
import SpotUtils from 'utils/spot';
import {CHECKOUT_FETCH_RATES} from 'checkout/checkout-actions';
import {SEARCH_REQUEST_SET_TERM} from 'search-request/search-request-actions';
import {SPOT_SET_TERM} from 'spot/spot-actions';
import {
    SPOTS_RADIAL_INCREMENT,
    SPOTS_TILED_INCREMENT,
    SPOTS_GET_MOBILE,
    SPOTS_GET_MOBILE_PAGINATION,
    SPOTS_GET_RADIAL,
    SPOTS_GET_AIRPORT
} from 'spots/spots-actions';
import {
    FILTERS_RESET,
    FILTERS_UPDATE
} from 'filters/filters-actions';

const ANALYTICS_SEARCH_UUID_COOKIE = 'sh-analytics-search-uuid';

export const analyticsMiddleware = store => next => action => {
    switch (action.type) {
        case SPOTS_RADIAL_INCREMENT:
        case SPOTS_TILED_INCREMENT:
        case `${SPOTS_GET_AIRPORT}_FULFILLED`:
        case `${SPOTS_GET_MOBILE}_FULFILLED`:
        case `${SPOTS_GET_MOBILE_PAGINATION}_FULFILLED`: {
            const {
                destination: {
                    data: {
                        airport,
                    },
                },
                city: {
                    data: {
                        display_name: cityName
                    }
                },
                search: {
                    data: {
                        currentIncrement,
                        destinationTitle,
                        geolocated
                    }
                },
                searchRequest: {
                    monthly,
                    referrer,
                    search_string: searchQuery,
                    latitude,
                    longitude,
                    starts,
                    ends
                },
                filters: {
                    activeAmenities
                }
            } = store.getState();

            if (currentIncrement === 1 || (action.meta && action.meta.isMobileRequest) || action.type === `${SPOTS_GET_AIRPORT}_FULFILLED`) {
                const analyticsSearchUUIDCookie = StorageUtils.get(ANALYTICS_SEARCH_UUID_COOKIE, 'cookie');
                const {
                    sessionUUID,
                    searchUUID,
                    action: trackingAction
                } = SearchTracking.getValues();
                const searchEventProps = {
                    'search query': searchQuery || destinationTitle,
                    'search city': cityName,
                    'session id': sessionUUID,
                    'search id': searchUUID,
                    'parking type': (airport)
                        ? 'airport'
                        : (monthly)
                            ? 'monthly'
                            : 'transient',
                    'search type': trackingAction,
                    'search source': (!isEmpty(referrer)) ? referrer : 'map',
                    'number of filters applied': activeAmenities.length,
                    'filters selected': activeAmenities.join(),
                    'search start time': moment(starts).format(Config.apiDateTimeFormat),
                    'search end time': moment(ends).format(Config.apiDateTimeFormat)
                };

                if (geolocated) {
                    searchEventProps['user location'] = `(${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
                }

                if (airport) {
                    searchEventProps['airport code search'] = airport.iata_code;
                }

                if (analyticsSearchUUIDCookie !== searchUUID) {
                    SegmentUtils.track({
                        event: 'Products Searched',
                        properties: searchEventProps
                    });

                    StorageUtils.set(ANALYTICS_SEARCH_UUID_COOKIE, searchUUID, 'cookie');
                }
            }

            break;
        }

        case SEARCH_REQUEST_SET_TERM: {
            const termString = (action.payload.monthly) ? 'monthly' : 'transient';

            GoogleRemarketingUtils.track({
                data: {
                    dynx_itemid: 's_NA', // eslint-disable-line camelcase
                    dynx_itemid2: termString, // eslint-disable-line camelcase
                    dynx_pagetype: 'searchresults' // eslint-disable-line camelcase
                }
            });

            break;
        }

        case SPOT_SET_TERM: {
            const {
                payload: {
                    newRate,
                    monthly
                }
            } = action;

            const googleRemarketingData = {
                dynx_itemid: `f_${store.getState().spot.selected.parking_spot_id}`, // eslint-disable-line camelcase
                dynx_itemid2: (monthly) ? 'monthly' : 'transient', // eslint-disable-line camelcase
                dynx_pagetype: 'conversionintent' // eslint-disable-line camelcase
            };

            if (newRate) {
                googleRemarketingData.dynx_totalvalue = (newRate.price / 100); // eslint-disable-line camelcase
            }

            GoogleRemarketingUtils.track({
                data: googleRemarketingData
            });

            GTMUtils.push({
                event: 'priceUpdated',
                rentalPrice: (newRate) ? (newRate.price / 100).toString() : 'N/A',
                currencyType: (newRate) ? newRate.currency_type : 'usd'
            });

            break;
        }

        case `${SPOTS_GET_RADIAL}_FULFILLED`: {
            const {
                search: {
                    data: {
                        searchType
                    }
                }
            } = store.getState();

            SegmentUtils.page({
                name: `${startCase(searchType)} Search`
            });

            break;
        }

        case `${CHECKOUT_FETCH_RATES}_FULFILLED`: {
            const {
                payload: {
                    data: {
                        data
                    }
                },
                meta: {
                    monthly,
                    spotId
                }
            } = action;
            const {
                checkout: {
                    previousRental
                },
                city: {
                    data: {
                        timezone
                    }
                },
                searchRequest: {
                    starts,
                    ends,
                    airport,
                    referrer,
                    pageType
                },
                spot: {
                    selected: selectedSpot
                }
            } = store.getState();
            const isAirportSpotDetail = (pageType === 'facility' && airport);
            const newRate = SpotUtils.prepare({
                spot: {
                    ...selectedSpot,
                    ...data
                },
                searchRequest: {
                    monthly
                },
                timezone
            }).selectedRate;
            const newPrice = (newRate) ? (newRate.price / 100).toString() : 'N/A';
            const newCurrencyType = (newRate) ? newRate.currency_type : 'usd';
            const startsMoment = (newRate && newRate.starts) ? moment(newRate.starts) : moment(starts);
            const endsMoment = (newRate && newRate.ends)
                ? moment(newRate.ends)
                : (monthly)
                    ? startsMoment.clone().add(1, 'month')
                    : moment(ends);
            const id = (selectedSpot) ? selectedSpot.spotId : spotId;
            const rentalType = (airport)
                ? 'airport'
                : (monthly)
                    ? 'monthly'
                    : 'transient';
            const googleRemarketingTrackingData = {
                dynx_itemid: `f_${id}`, // eslint-disable-line camelcase
                dynx_itemid2: rentalType, // eslint-disable-line camelcase
                dynx_pagetype: 'conversionintent' // eslint-disable-line camelcase
            };
            let referringScreen = null;

            if (newRate) {
                googleRemarketingTrackingData.dynx_totalvalue = newRate.price / 100; // eslint-disable-line camelcase
            }

            if (!isAirportSpotDetail) {
                GoogleRemarketingUtils.track({
                    data: googleRemarketingTrackingData
                });
            }

            switch (referrer) {
                case 'favorites':
                    referringScreen = 'favorite';
                    break;

                case 'list':
                    referringScreen = 'list';
                    break;

                case 'modal':
                    referringScreen = 'product details';
                    break;

                case 'widget':
                    referringScreen = 'widget';
                    break;

                default:
                    referringScreen = 'checkout';
                    break;
            }

            if (previousRental) {
                referringScreen = 'buy this spot again';
            }

            SegmentUtils.page({
                name: (isAirportSpotDetail) ? 'Spot Details' : 'Checkout',
                properties: {
                    price: newPrice,
                    'spot id': id,
                    'reservation duration': parseInt(moment.duration(endsMoment.diff(startsMoment)).format('m'), 10),
                    'time until reservation start': parseInt(moment.duration(startsMoment.diff(moment())).format('m'), 10),
                    'from screen': referringScreen,
                    'parking type': rentalType
                }
            });

            GTMUtils.push({
                event: 'priceUpdated',
                rentalPrice: newPrice,
                currencyType: newCurrencyType,
                spotId: id,
                rentalType
            });

            break;
        }

        case FILTERS_RESET: {
            SegmentUtils.track({
                event: 'Product List Filtered',
                properties: {
                    'number of filters applied': 0,
                    'filters selected': ''
                }
            });

            break;
        }

        case FILTERS_UPDATE: {
            const newState = {
                ...store.getState().filters,
                ...action.payload
            };

            let numActive = newState.activeAmenities.length;
            const allFilters = [...newState.activeAmenities];

            if (newState.activeReservationType !== 'all') {
                numActive++;
                allFilters.push(`availability-${newState.activeReservationType}`);
            }

            if (newState.price) {
                numActive++;
                allFilters.push('price');
            }

            SegmentUtils.track({
                event: 'Product List Filtered',
                properties: {
                    'number of filters applied': numActive,
                    'filters selected': allFilters.join()
                }
            });

            break;
        }
    }

    return next(action);
};
